/**
 * Report-library projection.
 *
 * Report pages render a read-model over the governed programmes the caller can
 * already see through the platform client. Nothing in this module persists:
 * the API and its SQL store are the only source of truth, so the projection is
 * rebuilt from a fresh read every time the reports scope mounts.
 *
 * This module used to own an IndexedDB store (`pds-programme-library`) that
 * outlived the session and was readable without a tenant scope. It is gone —
 * do not reintroduce a browser-side store here.
 *
 * @module reports/library
 */

import type { WorkflowState } from "@/server/types";
import type { Programme } from "@/types";
import { migrateProgramme } from "@/utils/migrate-programme";

import type { ProgrammeMetadata, ProgrammeStatus, StoredProgramme } from "./types";
import { DEFAULT_COLLEGE } from "./types";

/**
 * Build report metadata for a programme.
 *
 * - `college` defaults to DEFAULT_COLLEGE.
 * - `school` is taken from the programme's `discipline` field.
 */
export function buildMetadata(
  programme: Programme,
  overrides?: Partial<ProgrammeMetadata>,
): ProgrammeMetadata {
  return {
    college: overrides?.college ?? DEFAULT_COLLEGE,
    school: overrides?.school || programme.discipline || "Unknown",
    academicYear: overrides?.academicYear,
    status: overrides?.status,
  };
}

/** One governed programme as read back from the platform client. */
export interface BackendProgrammeLibraryInput {
  programme: Programme;
  tenantId: string;
  tenantName: string;
  revision: number;
  workflowState: WorkflowState;
  leadUserId?: string;
  leadUserName?: string;
  leadLastOpenedAt?: string | null;
}

/** Map governed workflow state onto the coarser report status model. */
export function reportStatusForWorkflow(workflowState: WorkflowState): ProgrammeStatus {
  if (workflowState === "Archived") {
    return "dormant";
  }
  if (
    workflowState === "Approved" ||
    workflowState === "Final Descriptor Signed Off" ||
    workflowState === "Exported and Locked"
  ) {
    return "active";
  }
  return "in-validation";
}

/**
 * Project one tenant's governed programmes into report records.
 *
 * Records from another tenant are a programming error rather than something to
 * filter away quietly: the caller passes the tenant it read them under, so a
 * mismatch means the read itself was mis-scoped.
 */
export function toReportProgrammes(
  tenantId: string,
  incoming: BackendProgrammeLibraryInput[],
  syncedAt: string,
): StoredProgramme[] {
  return incoming.map((record) => {
    if (record.tenantId !== tenantId) {
      throw new Error(`Cannot report on programme ${record.programme.id} from another tenant`);
    }

    const programme = migrateProgramme(record.programme) as Programme;

    return {
      libraryId: programme.id,
      data: programme,
      metadata: buildMetadata(programme, {
        college: record.tenantName,
        school: programme.discipline ?? "Unknown",
        status: reportStatusForWorkflow(record.workflowState),
      }),
      syncedAt,
      origin: {
        kind: "backend",
        tenantId,
        revision: record.revision,
        workflowState: record.workflowState,
        leadUserId: record.leadUserId,
        leadUserName: record.leadUserName,
        leadLastOpenedAt: record.leadLastOpenedAt,
      },
    };
  });
}
